import React from 'react'
import styled, { css } from 'styled-components'

import { EmptyStateProps } from '../EmptyState/EmptyState'
import { Skeleton } from '../Skeleton'
import { getFont, getTypographyStyles, getVariantStyles } from '../Typography'
import { TBody } from './TBody'
import { StyledTd } from './Td'
import { Th } from './Th'
import { THead } from './THead'
import { Tr } from './Tr'

type TableVariant = 'default' | 'borderless'

export type TableProps = React.ComponentPropsWithRef<'table'> & {
  loading?: boolean
  pagination?: React.ReactNode
  pageSize?: number
  loadingColumns?: number
  scrollable?: boolean
  scrollableX?: boolean
  variant?: TableVariant
}

export type TBodyProps = React.ComponentPropsWithoutRef<'tbody'> & {
  emptyVariant?: EmptyStateProps['variant']
  emptySize?: 'default' | 'small'
  emptyMessage?: string
  EmptyState?: React.ReactNode
  emptyStateAndMessage?: boolean
}

export type ThProps = React.ComponentPropsWithoutRef<'th'> & {
  sortDirection?: 'asc' | 'desc'
  onClick?: React.MouseEventHandler
}

export type TFootProps = React.ComponentPropsWithoutRef<'tfoot'> & {}

export function TFoot(props: TFootProps) {
  return <tfoot {...props} />
}

export const StyledTable = styled.table<{ variant?: TableVariant }>`
  min-width: 100%;
  ${getTypographyStyles('body')}
  border-spacing: 0;
  border-collapse: separate;

  [role='columnheader'] {
    ${getVariantStyles('h4')}
    text-align: left;
    border-bottom: 1px solid var(--border);
    background: var(--surface-secondary);
    white-space: nowrap;

    ${(props) =>
      props.variant === 'borderless' &&
      `
      padding: 0;
      background: none;
      border-bottom: none;
    `}

    button {
      ${getVariantStyles('h4')}
      font-family: ${getFont('body')};
      font-display: swap;
      border: 0;
      outline: 0;
      background: transparent;
      display: flex;
      align-items: center;
      padding: 0;
      margin: 0;

      &:focus-visible {
        color: var(--brand-primary);
      }
    }
  }

  [role='cell'] {
    ${getVariantStyles('body')}
    background-color: var(--surface-primary);
    border-bottom: 1px solid var(--border);

    ${(props) =>
      props.variant === 'borderless' &&
      `
      border-bottom: none;
    `}
  }

  [role='row']:last-of-type [role='cell'] {
    border-bottom: none;
  }

  [role='cell'],
  [role='columnheader'] {
    padding: 14px 12px;

    ${(props) =>
      props.variant === 'borderless' &&
      `
      padding: 0 12px 14px;
    `}
  }

  [role='cell']:first-of-type,
  [role='columnheader']:first-of-type {
    padding-left: 24px;

    ${(props) =>
      props.variant === 'borderless' &&
      `
      padding-left: 0;
    `}
  }

  [role='cell']:last-of-type,
  [role='columnheader']:last-of-type {
    padding-right: 24px;

    ${(props) =>
      props.variant === 'borderless' &&
      `
      padding-right: 0;
    `}
  }

  thead {
    > tr {
      th:first-child {
        border-top-left-radius: 6px;
      }
      th:last-child {
        border-top-right-radius: 6px;
      }
    }
  }

  tbody {
    > tr:last-child {
      td:first-child {
        border-bottom-left-radius: 6px;
      }
      td:last-child {
        border-bottom-right-radius: 6px;
      }
    }
  }
`

const Table = ({
  loading,
  pagination,
  pageSize,
  children,
  loadingColumns,
  scrollable,
  scrollableX,
  variant,
  ...props
}: TableProps) => {
  const _pageSize = pageSize ?? 10

  return (
    <div
      css={css`
        border-radius: 8px;
        border: 2px solid var(--border);

        ${scrollable && `overflow-y: auto;`}
        ${scrollableX && `overflow-x: auto;`}
        
        ${
          variant === 'borderless' &&
          `
          border: none;
        `
        }

        nav {
          border-top: 1px solid var(--border);
          background: var(--surface-primary);
          width: 100%;
          padding: 12px 24px;
        }
      `}
      data-testid='table'
    >
      {loading ? (
        <>
          <StyledTable variant={variant} {...props}>
            <THead css='width: 100%;'>
              <Tr>
                {Array.from(Array(loadingColumns ?? 4)).map((_, idx) => (
                  <Th
                    key={`loading-column column-${
                      // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                      idx
                    }`}
                  >
                    <Skeleton />
                  </Th>
                ))}
              </Tr>
            </THead>
            <TBody>
              {Array.from(Array(_pageSize)).map((_, x) => (
                <Tr
                  key={`loading-row row-${
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    x
                  }`}
                >
                  {Array.from(Array(loadingColumns ?? 4)).map((_, y) =>
                    y === 0 ? (
                      <StyledTd
                        key={`loading-cell cell-${x}-${
                          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                          y
                        }`}
                      >
                        <Skeleton style={{ height: '20px' }} />
                        <Skeleton style={{ height: '10px', width: '50%' }} />
                      </StyledTd>
                    ) : (
                      <StyledTd
                        key={`loading-cell cell-${x}-${
                          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                          y
                        }`}
                      />
                    )
                  )}
                </Tr>
              ))}
            </TBody>
          </StyledTable>
          {pagination ? (
            <div
              css={`
                display: flex;
                justify-content: space-between;
              `}
            >
              <Skeleton />
            </div>
          ) : null}
        </>
      ) : (
        <>
          <StyledTable variant={variant} {...props}>
            {children}
          </StyledTable>
          {pagination || null}
        </>
      )}
    </div>
  )
}

export default Table
